import { useEffect, useState } from "react";
import { fetchInterviews } from "@/lib/strapi";

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 12,
  pageCount: 1,
  total: 0,
};

const mergeInterviews = (currentInterviews, incomingInterviews) => {
  const seenInterviewIds = new Set(currentInterviews.map((interview) => interview.slug || interview.id));
  const uniqueIncomingInterviews = incomingInterviews.filter((interview) => {
    const key = interview.slug || interview.id;
    if (!key || seenInterviewIds.has(key)) {
      return false;
    }

    seenInterviewIds.add(key);
    return true;
  });

  return [...currentInterviews, ...uniqueIncomingInterviews];
};

export function useInterviews(limit = 12) {
  const [interviews, setInterviews] = useState([]);
  const [pagination, setPagination] = useState({ ...DEFAULT_PAGINATION, pageSize: limit });
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    const loadInterviews = async () => {
      try {
        setIsLoading(true);
        setError("");
        const result = await fetchInterviews({ page: 1, pageSize: limit });
        if (mounted) {
          setInterviews(result?.items || []);
          setPagination(result?.pagination || { ...DEFAULT_PAGINATION, pageSize: limit });
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : "Failed to load interviews");
          setInterviews([]);
          setPagination({ ...DEFAULT_PAGINATION, pageSize: limit });
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    loadInterviews();

    return () => {
      mounted = false;
    };
  }, [limit]);

  const loadMore = async () => {
    if (isLoading || isLoadingMore || pagination.page >= pagination.pageCount) {
      return;
    }

    try {
      setIsLoadingMore(true);
      setError("");
      const result = await fetchInterviews({ page: pagination.page + 1, pageSize: limit });
      setInterviews((currentInterviews) => mergeInterviews(currentInterviews, result?.items || []));
      setPagination(result?.pagination || pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load interviews");
    } finally {
      setIsLoadingMore(false);
    }
  };

  return {
    interviews,
    pagination,
    isLoading,
    isLoadingMore,
    error,
    hasMore: pagination.page < pagination.pageCount,
    loadMore,
  };
}
